// src/pages/facturas/Facturas.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext } from "react";
import { toast } from '../../utils/toast';
import { Button, Box, Typography, Stack, Paper, alpha } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import PageLayout from "../../components/layout/PageLayout";
import ConfiguracionFacturasModal from "../../components/module/facturas/ConfiguracionFacturasModal";
import ConfiguracionFacturasTable from "../../components/module/facturas/ConfiguracionFacturasTable";
import { ConfiguracionFactura } from "../../components/module/facturas/types/facturas.types";
import { Description as DescriptionIcon, Add as AddIcon, Warning } from "@mui/icons-material";
import { facturasApi } from "../../services/api/facturasApi";
import { DarkModeContext } from "../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", accent: "#2383E2", accentBg: "#E8F0FE", error: "#E03E3E", errorBg: "#FBE4E4", shadow: "0 1px 3px rgba(0,0,0,0.04)", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", accent: "#529CCA", accentBg: "#143A52", error: "#D44C47", errorBg: "#3D2020", shadow: "0 1px 3px rgba(0,0,0,0.2)", hover: "rgba(255,255,255,0.04)" },
};

const Facturas = () => {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [canCreateNew, setCanCreateNew] = useState(true);
  const [configuraciones, setConfiguraciones] = useState<ConfiguracionFactura[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<ConfiguracionFactura | undefined>();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  useEffect(() => { cargarConfiguraciones(); }, []);

  const cargarConfiguraciones = async () => {
    try {
      const response = await facturasApi.obtenerConfiguracion();
      if (response.data) {
        setConfiguraciones([response.data]);
        setCanCreateNew(response.data.Id === 0 || !response.data);
      } else if (response.message) {
        setCanCreateNew(true);
        toast.info("Información");
      }
    } catch {
      setCanCreateNew(true);
      toast.error("Error al cargar las configuraciones");
    }
  };

  if (user?.Perfil !== "ADMINISTRADOR") {
    return (
      <PageLayout icon={DescriptionIcon} title="Configuración de Facturas">
        <Box sx={{ p: 3 }}>
          <Box sx={{ p: 3, borderRadius: "10px", border: `1px solid ${p.error}`, bgcolor: p.errorBg, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Warning sx={{ fontSize: 20, color: p.error }} />
            <Typography sx={{ fontSize: "0.88rem", color: p.error, fontWeight: 500 }}>No tiene permisos para acceder a esta sección</Typography>
          </Box>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout icon={DescriptionIcon} title="Configuración de Facturas">
      <Box sx={{ p: 3, fontFamily: '"IBM Plex Sans", -apple-system, sans-serif' }}>
        <Paper elevation={0} sx={{ border: `1px solid ${p.border}`, borderRadius: "12px", bgcolor: p.bg, boxShadow: p.shadow, overflow: "hidden" }}>
          {/* Header */}
          <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}`, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text }}>Configuración de Facturas</Typography>
              <Typography sx={{ fontSize: "0.82rem", color: p.textT, mt: 0.3 }}>Administra la numeración y configuración de facturas</Typography>
            </Box>
            <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => { setSelectedConfig(undefined); setModalOpen(true); }} disabled={!canCreateNew}
              sx={{
                bgcolor: p.accent, color: "#FFF", px: 2.5, py: 1, fontSize: "0.84rem", fontWeight: 600, borderRadius: "8px",
                "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" },
                "&.Mui-disabled": { bgcolor: alpha(p.accent, 0.15), color: alpha(p.text, 0.3) },
              }}>
              Nueva Configuración
            </Button>
          </Box>

          {/* Table */}
          <Box sx={{ p: 2 }}>
            <ConfiguracionFacturasTable configuraciones={configuraciones} onEdit={(cfg) => { setSelectedConfig(cfg); setModalOpen(true); }} />
          </Box>
        </Paper>

        <ConfiguracionFacturasModal open={modalOpen} onClose={() => setModalOpen(false)} initialData={selectedConfig} recargarDatos={cargarConfiguraciones} />
      </Box>
    </PageLayout>
  );
};

export default Facturas;
